import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Emergent Google Auth callback handler.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Flow (called by App.js when window.location.hash contains 'session_id='):
 *   1. Read session_id from URL fragment synchronously.
 *   2. POST it to /api/auth/google/callback to exchange for our JWT.
 *   3. Store JWT via AuthContext.googleCallback().
 *   4. Navigate to /dashboard, replacing history so the hash is gone.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { googleCallback } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : '';

    if (!sessionId) {
      setError('No session ID returned from Google. Please try again.');
      return;
    }

    googleCallback(sessionId)
      .then(() => {
        // Clear the hash and land on dashboard.
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        setError(err.message || 'Google sign-in failed. Please try again.');
      });
  }, [googleCallback, navigate]);

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center px-6" data-testid="auth-callback">
      <div className="max-w-md text-center">
        {error ? (
          <>
            <h1 className="text-2xl font-serif text-[#1A2F4A] mb-3">Sign-in issue</h1>
            <p className="text-sm text-red-600 mb-6" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => navigate('/login', { replace: true })}
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#1A2F4A] rounded-full px-6 py-3 text-sm font-bold hover:bg-[#1A2F4A] hover:text-[#D4AF37] transition-colors"
              data-testid="auth-callback-back"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-testid="auth-callback-spinner" />
            <p className="text-sm text-[#6B4E37]">Finalising your Selora session…</p>
          </>
        )}
      </div>
    </div>
  );
}
